<template>
    <div
        id="product-type-groups-page"
        :class="{ dragging }"
    >
        <sync-progress
            :state="syncState"
            :animation-speed="1"
        />
        <div class="checkbox-container">
            <ui5-label for="showProductTypesCheckbox">
                {{ trans('pages.product_type_groups.show_product_types') }}
            </ui5-label>
            <ui5-checkbox
                id="showProductTypesCheckbox"
                :checked="showProductTypes"
                @change="showProductTypes = $event.target.checked"
            />
        </div>
        <spinner-text v-if="loading">
            {{ trans('pages.product_type_groups.loading') }}
        </spinner-text>
        <div v-else-if="nestedList.length">
            <product-type-groups-nested
                :show-product-types="showProductTypes"
                :product-types="productTypes"
                v-model="nestedList"
                @drag-start="dragging = true"
                @drag-end="onDragEnd"
                :languages="languages"
            />
        </div>
        <div v-else>
            {{ trans('pages.product_type_groups.no_groups') }}
        </div>

        <div
            class="debug"
            v-if="isDebug"
        >
            <h3>Debug Info</h3>
            <h4>Nested List</h4>
            <pre>{{ nestedList }}</pre>
            <h4>Hierarchy Payload</h4>
            <pre>{{ hierarchyPayload }}</pre>
            <h4>Product Type Groups</h4>
            <pre>{{ $store.state.productTypeGroups.productTypeGroups }}</pre>
        </div>
    </div>
</template>

<script>
import ProductTypeGroupsNested from '../components/productTypeGroups/ProductTypeGroupsNested.vue';
import { showErrorMessage } from '../dialogs';
import localization from '../mixins/localization';
import IsDebugMixin from '../mixins/is-debug';
import productTypeApi from '../api/productType';
import languageApi from '../api/language';
import SpinnerText from '../components/loader/SpinnerText';
import SyncProgress from '../components/loader/SyncProgress.vue';

export default {
    name: 'ProductTypeGroupPage',
    mixins: [localization, IsDebugMixin],
    display: 'Nested',
    order: 15,
    components: {
        ProductTypeGroupsNested,
        SpinnerText,
        SyncProgress,
    },
    data() {
        return {
            loading: false,
            productTypes: null,
            dragging: false,
            showProductTypes: false,
            languages: null,
        };
    },
    computed: {
        hierarchyPayload() {
            return this.$store.getters['productTypeGroups/getHierarchyPayload'];
        },
        nestedList: {
            get() {
                return this.$store.state.productTypeGroups.nestedList;
            },
            set(value) {
                this.$store.dispatch('productTypeGroups/updateTree', {
                    newList: value,
                });
            },
        },
        syncState() {
            return this.$store.getters['productTypeGroups/getSyncState'];
        },
    },
    async mounted() {
        await this.load();
    },
    methods: {
        async load() {
            this.loading = true;
            try {
                languageApi.fetchAll().then((request) => {
                    this.languages = request.data.data;
                });

                const productTypesRes =
                    await productTypeApi.fetchProductTypes();
                this.productTypes = (productTypesRes.data ?? []).sort((a, b) =>
                    (a.sortOrder || Infinity) > (b.sortOrder || Infinity)
                        ? 1
                        : -1
                );
                await this.$store.dispatch('productTypeGroups/loadFlatData');
                await this.$store.dispatch('productTypeGroups/initializeTree');
            } catch (error) {
                console.error('Error loading product type groups:', error);
                showErrorMessage(
                    this.trans(
                        'pages.product_type_groups.unknown_loading_error'
                    )
                );
            } finally {
                this.loading = false;
            }
        },
        onDragEnd() {
            this.dragging = false;
            this.nestedList = [...this.nestedList];
        },
    },
};
</script>

<style
    lang="scss"
    scoped
>
.sync-progress {
    position: fixed;
    right: 0.5em;
    font-size: 2em;
    bottom: 0.5em;
}
.debug {
    font-size: 0.7em;
}
pre {
    max-height: 200px;
    overflow: scroll;
}

.checkbox-container {
    position: absolute;
    right: 2em;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 0;
    margin-top: -0.8em;
    font-size: 0.5em;

    ui5-label {
        color: #333;
    }

    ui5-checkbox {
        color: #333;
    }
}
</style>
